import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import qs from 'querystringify';
import {
  Box,
  Button,
  Card,
  Flex,
  ButtonGroup,
  Page,
  Text,
  CONTENT_TYPE,
} from '../../components';
import PlanForm from './PlanForm';
import EditBenefit from './benefits/EditBenefit';
import NewBenefit from './benefits/NewBenefit';
import DeletePlan from './DeletePlan';
import DeletePlanBenefit from './benefits/DeletePlanBenefit';
import PlanBenefitCategory from './benefits/planBenefitCategory/PlanBenefitCategory';
import api from '../../utils/services';
import { getFeatures, getSelectedLanguage } from '../../modules/selectors';

const propTypes = {
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      carrierId: PropTypes.string,
      planId: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  features: PropTypes.shape({
    liquidTemplates: PropTypes.bool,
    richText: PropTypes.bool,
    staticHtml: PropTypes.bool,
  }).isRequired,
  selectedLanguage: PropTypes.shape({
    languageId: PropTypes.number,
  }).isRequired,
};

class EditPlan extends Component {
  constructor(props) {
    super(props);
    this.state = {
      deleteBenefitId: null,
      deleteError: null,
      isOpenAddPlanBenefit: false,
      isOpenPlanBenefitCategories: false,
      isOpenDeletePlan: false,
      isOpenDeletePlanBenefit: false,
      planLoading: true,
      benefitCategoryLoading: true,
      plan: {},
      planEditingError: null,
      planEditingSuccess: null,
      saving: false,
      benefitCategories: [],
    };
  }

  componentDidMount() {
    const { match: { params: { planId } }, selectedLanguage: { languageId } } = this.props;
    api.getPlanByIdAndLanguage(planId, qs.stringify({ languageId }, true))
      .then((response) => {
        // order the benefits by the order column and then set the state
        response.planBenefits = response.planBenefits
          .sort((a, b) => a.id - b.id)
          .sort((a, b) => a.order - b.order);
        response.planBenefits = response.planBenefits.map((benefitObj) => {
          const benefit = benefitObj;
          benefit.type = benefit.type || CONTENT_TYPE.PLAIN;
          return benefit;
        });
        this.setState({
          plan: response,
          planLoading: false,
        });
      });

    api.getPlanBenefitCategories()
      .then((response) => {
        // order the benefits by the order column and then set the state
        response.push({ id: 0, name: 'None' });
        const sortResponse = [...response.sort((a, b) => a.id - b.id)];
        this.setState({
          benefitCategoryLoading: false,
          benefitCategories: [...sortResponse.map(x => ({
            id: x.id,
            value: x.name,
            text: x.name,
          }))],
        });
      });
  }

  handleChange = (event, benefitId) => {
    const { name, value } = event.target;
    this.setState((state) => {
      const { plan } = state;
      const newBenefits = plan.planBenefits.map((benefitObj) => {
        const benefit = benefitObj;
        if (benefit.id === benefitId) {
          if (name === 'type') {
            // clear the value as it is not compatible with different types
            benefit.value = '';
          }
          return {
            ...benefit,
            [name]: value,
          };
        }
        return benefit;
      });
      return {
        plan: {
          ...plan,
          planBenefits: newBenefits,
        },
      };
    });
  }

  handleClose = () => {
    this.setState({
      isOpenAddPlanBenefit: false,
      isOpenDeletePlan: false,
      isOpenDeletePlanBenefit: false,
    });
  }

  handleBenefitCategoryClose = () => {
    this.setState({
      isOpenPlanBenefitCategories: false,
    });
  }

  handleDeletePlan = (planID) => {
    const { history, match: { params: { carrierId } } } = this.props;
    api.deletePlan(planID)
      .then(() => {
        history.push(`/carriers/${carrierId}/plans`);
      })
      .catch(() => {
        this.setState({ deleteError: 'Error deleting the plan, try again later.' });
      });
  }

  handleSaveBenefits = () => {
    const { plan, planEditingError, planEditingSuccess } = this.state;
    this.setState({ saving: true });
    if (planEditingSuccess || planEditingError) {
      this.setState({ planEditingSuccess: null, planEditingError: null });
    }
    api.updateBatchPlanBenefits(plan.planBenefits)
      .then(() => {
        this.setState({ planEditingSuccess: 'Plan benefits saved successfully.', saving: false });
      })
      .catch(() => {
        this.setState({ planEditingError: 'Unable to edit the plan benefits, try again later.', saving: false });
      });
  }

  handleOpenDeletePlanBenefit = (benefitId) => {
    this.setState({
      deleteBenefitId: benefitId,
      isOpenDeletePlanBenefit: true,
    });
  }

  handleDeletePlanBenefit = (benefitID) => {
    api.deletePlanBenefit(benefitID)
      .then(() => {
        const { plan } = this.state;
        this.setState((state) => {
          const planBenefits = plan.planBenefits.filter(pb => pb.id !== benefitID);
          return {
            plan: {
              ...state.plan,
              planBenefits,
            },
            isOpenDeletePlanBenefit: false,
          };
        });
      })
      .catch(() => {
        this.setState({ deleteError: 'Error deleting the plan benefit, try again later.' });
      });
  }

  // Once a benefit is added, it should update the state and trigger a render.
  handleBenefitAdded = (benefit) => {
    this.setState((state) => {
      const { planBenefits } = state.plan;
      planBenefits.push(benefit);
      return {
        plan: {
          ...state.plan,
          planBenefits,
        },
      };
    });
  }

  render() {
    const {
      match: { params: { planId, carrierId } },
      selectedLanguage: { languageId },
    } = this.props;
    const {
      deleteBenefitId,
      deleteError,
      isOpenAddPlanBenefit,
      isOpenDeletePlan,
      isOpenDeletePlanBenefit,
      isOpenPlanBenefitCategories,
      planLoading,
      benefitCategoryLoading,
      plan,
      planEditingError,
      planEditingSuccess,
      saving,
      benefitCategories,
    } = this.state;

    const { features } = this.props;
    const contentDropDownOptions = [{ text: 'Plain Text', value: CONTENT_TYPE.PLAIN, secondaryText: 'Easy, Fast' }];
    if (features.richText) {
      contentDropDownOptions.push({ text: 'Rich Text', value: CONTENT_TYPE.QUILL, secondaryText: 'Easy, Powerful' });
    }
    if (features.staticHtml) {
      contentDropDownOptions.push({ text: 'Static HTML', value: CONTENT_TYPE.HTML, secondaryText: 'Advanced, Fast' });
    }
    if (features.liquidTemplates) {
      contentDropDownOptions.push({ text: 'Liquid HTML', value: CONTENT_TYPE.LIQUID, secondaryText: 'Advanced, Powerful' });
    }

    const loading = planLoading || benefitCategoryLoading;

    const planBenefitsMarkup = (plan.planBenefits || []).map(benefit => (
      <EditBenefit
        key={benefit.id}
        benefit={{
          ...benefit,
          order: benefit.order.toString(),
          languageId: benefit.languageId.toString(),
        }}
        onChange={this.handleChange}
        onDelete={this.handleOpenDeletePlanBenefit}
        typeDropDownOptions={contentDropDownOptions}
        benefitCategoryDropDownOptions={benefitCategories}
      />
    ));

    return (
      <Page
        breadcrumbs={[{ content: 'View Plan', href: `/carriers/${carrierId}/plans/${planId}?languageId=${languageId}` }]}
        title={plan.name || 'Loading...'}
        fullWidth
      >
        <PlanForm planId={planId} languageId={languageId} />
        <Box height="1em" />
        <Card loading={loading}>
          <Card.Title>Plan Benefits</Card.Title>
          <Card.Block>
            {planEditingError && <Text color="red">{planEditingError}</Text>}
            {planEditingSuccess && <Text color="green">{planEditingSuccess}</Text>}
            {plan && plan.planBenefits && plan.planBenefits.length > 0
              && !loading ? (
                <React.Fragment>
                  <table style={{ width: '-webkit-fill-available' }}>
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Value</th>
                        <th>Value Type</th>
                        <th>Category</th>
                        <th>Subcategory</th>
                        <th>Benefit Category</th>
                        <th>Order</th>
                        <th>Language</th>
                      </tr>
                    </thead>
                    <tbody>
                      {planBenefitsMarkup}
                    </tbody>
                  </table>
                  <Flex paddingTop="1em">
                    <Flex.Item marginStart="auto">
                      <ButtonGroup>
                        <Button
                          onClick={() => this.setState({ isOpenPlanBenefitCategories: true })}
                        >
                          Add Benefit Categories
                        </Button>
                        <Button onClick={() => this.setState({ isOpenAddPlanBenefit: true })}>
                          Add Benefit
                        </Button>
                        <Button primary type="submit" loading={saving} onClick={this.handleSaveBenefits}>
                          Save Benefits
                        </Button>
                      </ButtonGroup>
                    </Flex.Item>
                  </Flex>
                </React.Fragment>
              ) : <Text>No plan benefits</Text>}
          </Card.Block>
        </Card>
        <Box height="1em" />

        <Button variant="danger" primary onClick={() => this.setState({ isOpenDeletePlan: true })}>Delete Plan</Button>

        <NewBenefit
          planId={parseInt(planId, 10)}
          isOpen={isOpenAddPlanBenefit}
          onClose={this.handleClose}
          typeDropDownOptions={contentDropDownOptions}
          benefitCategoryDropDownOptions={benefitCategories}
          onBenefitAdded={this.handleBenefitAdded}
        />
        <DeletePlan
          closeHandler={this.handleClose}
          isOpen={isOpenDeletePlan}
          deleteHandler={() => this.handleDeletePlan(planId)}
          planName={plan.name}
          deleteError={deleteError}
        />
        <DeletePlanBenefit
          closeHandler={this.handleClose}
          isOpen={isOpenDeletePlanBenefit}
          deleteHandler={() => this.handleDeletePlanBenefit(deleteBenefitId)}
          deleteError={deleteError}
        />
        <PlanBenefitCategory
          isOpen={isOpenPlanBenefitCategories}
          onClose={this.handleBenefitCategoryClose}
        />
      </Page>
    );
  }
}

function mapStateToProps(state) {
  return {
    features: getFeatures(state),
    selectedLanguage: getSelectedLanguage(state),
  };
}
EditPlan.propTypes = propTypes;
export default withRouter(connect(mapStateToProps)(EditPlan));
